import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { fetchAdminAcquisition } from "../features/admin/slice-extensions/admin-slice-extension";
import {
  selectAdminAcquisition,
  selectAdminAcquisitionStatus,
} from "../features/admin/admin-slice";
import { LoadingStatus } from "../features/sessions/models/LoadingStatus";
import LeftMenu from "../components/LeftMenu";
import Breadcrumbs from "../components/Breadcrumbs";
import AdminTabs from "../components/admin/AdminTabs";
import AdminAcquisitionSummary from "../components/admin/AdminAcquisitionSummary";
import AdminAcquisitionSourcesTable from "../components/admin/AdminAcquisitionSourcesTable";
import {
  AdminAcquisitionDaysTable,
  AdminAcquisitionPagesTable,
  AdminAcquisitionPlacementsTable,
} from "../components/admin/AdminAcquisitionTables";

const RANGE_OPTIONS = [7, 30, 90];

/** The acquisition funnel: marketing-site hand-offs to the sign-up screen versus accounts created. */
const AdminAcquisitionPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const acquisition = useAppSelector(selectAdminAcquisition);
  const status = useAppSelector(selectAdminAcquisitionStatus);
  const [days, setDays] = useState(30);

  useEffect(() => {
    dispatch(fetchAdminAcquisition({ days }));
  }, [dispatch, days]);

  const isLoading = status === LoadingStatus.Loading;
  const isFailed = status === LoadingStatus.Failed;

  return (
    <section>
      <div className="flex h-full gap-4">
        <LeftMenu />
        <div className="w-full p-4 pb-[120px] lg:px-10 lg:pb-10">
          <div className="lg:max-w-[1280px]">
            <Breadcrumbs />
            <div className="space-y-4 py-6 lg:space-y-6">
              <div>
                <h1>{t("admin.acquisition.title")}</h1>
                <p className="text-grey-40 dark:text-grey-70">{t("admin.acquisition.subtitle")}</p>
              </div>

              <AdminTabs />

              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="max-w-2xl text-sm text-grey-50 dark:text-grey-60">
                  {t("admin.acquisition.explainer")}
                </p>
                <nav className="flex gap-2" aria-label={t("admin.acquisition.range")}>
                  {RANGE_OPTIONS.map((option) => (
                    <button
                      key={option}
                      type="button"
                      aria-pressed={option === days}
                      onClick={() => setDays(option)}
                      className={`rounded-full px-4 py-1.5 text-sm font-medium ${
                        option === days
                          ? "bg-primary-95 text-primary-40 dark:bg-primary-30 dark:text-primary-90"
                          : "text-grey-40 hover:bg-grey-95 dark:text-grey-70 dark:hover:bg-primary-20"
                      }`}
                    >
                      {t("admin.acquisition.lastDays", { count: option })}
                    </button>
                  ))}
                </nav>
              </div>

              {isFailed && (
                <div className="rounded-lg bg-red-50 dark:bg-red-900/20 p-4 text-red-700 dark:text-red-300">
                  <p>{t("admin.acquisition.errorLoading")}</p>
                </div>
              )}

              {isLoading && !acquisition ? (
                <p className="py-10 text-center text-grey-40 dark:text-grey-70">
                  {t("admin.loading")}
                </p>
              ) : (
                acquisition && (
                  <>
                    <AdminAcquisitionSummary acquisition={acquisition} />
                    <h2 className="text-lg font-semibold text-grey-10 dark:text-white">
                      {t("admin.acquisition.bySource")}
                    </h2>
                    <AdminAcquisitionSourcesTable sources={acquisition.sources} />
                    <h2 className="text-lg font-semibold text-grey-10 dark:text-white">
                      {t("admin.acquisition.byPage")}
                    </h2>
                    <AdminAcquisitionPagesTable pages={acquisition.pages} />
                    <h2 className="text-lg font-semibold text-grey-10 dark:text-white">
                      {t("admin.acquisition.byPlacement")}
                    </h2>
                    <AdminAcquisitionPlacementsTable placements={acquisition.placements} />
                    <h2 className="text-lg font-semibold text-grey-10 dark:text-white">
                      {t("admin.acquisition.byDay")}
                    </h2>
                    <AdminAcquisitionDaysTable days={acquisition.days} />
                  </>
                )
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AdminAcquisitionPage;
